import React, { useState } from "react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { Bullet, Open } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { useDisconnectService } from "@/entities/settings/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { AuthRedirectProps } from "@/services/auth/AuthRedirect";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";

const TRANSLATION_PREFIX = "connectedAccounts";

interface ServiceActionProps {
  name: string;
  serviceId: string;
  onAuthRedirect: (props: AuthRedirectProps) => void;
  registerTo: string;
  registerServices?: string[];
  hasTokens?: boolean;
  allTokensValid?: boolean;
  isTokenLoading: boolean;
  // A failed read keeps the last status, which may no longer be true.
  isTokenError?: boolean;
  disableServiceDisconnection?: boolean;
  disableConnect?: boolean;
  disableConnectTooltip?: string;
}

const ServiceAction: React.FC<ServiceActionProps> = ({
  name,
  serviceId,
  onAuthRedirect,
  registerTo,
  registerServices,
  hasTokens,
  allTokensValid,
  isTokenLoading,
  isTokenError,
  disableServiceDisconnection,
  disableConnect,
  disableConnectTooltip
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { disconnect: disconnectService } = useDisconnectService(serviceId);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

  const isConnected = hasTokens && allTokensValid;

  const handleConnect = () => {
    const body = registerServices ? { services: registerServices } : undefined;

    onAuthRedirect({
      to: registerTo,
      method: "POST",
      body
    });
  };

  const handleDisconnect = async () => {
    try {
      await disconnectService();
    } catch (error) {
      console.error(`${name} disconnection error:`, error);
      toast.error(t(`${TRANSLATION_PREFIX}.disconnectError`, { name }));
    }
    // A failed disconnect may still have removed the token, so re-read it.
    await handleRefreshTokenStatus().catch(() => {});
  };

  const handleRefreshTokenStatus = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["settings/tokens/all"]
    });
  };

  const handleOpenDisconnectModal = () => {
    setIsDisconnectModalOpen(true);
  };

  const handleConfirmDisconnect = () => {
    setIsDisconnectModalOpen(false);
    handleDisconnect();
  };

  const dropdownOptions: DetailedViewOptions = {
    disconnect: {
      action: handleOpenDisconnectModal
    }
  };

  return (
    <>
      <div className="flex items-center gap-1">
        {isTokenLoading ? (
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_PREFIX}.checking`)}
          </Text>
        ) : isTokenError ? (
          <Text variant="smBodyError" data-test="service-status-error">
            {t("integrations.statusError")}
          </Text>
        ) : isConnected ? (
          <>
            <div className="flex items-center gap-[6px] px-2 py-[6px]">
              <IconContainer size="xs" color="positive" className="pb-[3px]">
                <Bullet />
              </IconContainer>
              <Text>{t(`${TRANSLATION_PREFIX}.connected`)}</Text>
            </div>
            <OptionsDropdownMenu
              options={dropdownOptions}
              tPrefix={`${TRANSLATION_PREFIX}.menu`}
              buttonVariant="iconNoBg"
              iconSize="lg"
              iconColor="secondary"
              className="hover:bg-surface-hover-strong"
              disabled={disableServiceDisconnection}
              tooltipText={
                disableServiceDisconnection
                  ? t(`${TRANSLATION_PREFIX}.disconnectTooltip`, {
                      service: name
                    })
                  : undefined
              }
            />
          </>
        ) : disableConnect ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="secondary" disabled className="gap-2">
                  <IconContainer size="sm" color="disabled">
                    <Open />
                  </IconContainer>
                  {t(`${TRANSLATION_PREFIX}.connect`)}
                </Button>
              </span>
            </TooltipTrigger>
            {disableConnectTooltip && (
              <TooltipContent>{disableConnectTooltip}</TooltipContent>
            )}
          </Tooltip>
        ) : (
          <Button variant="secondary" onClick={handleConnect} className="gap-2">
            <IconContainer size="sm">
              <Open />
            </IconContainer>
            {t(`${TRANSLATION_PREFIX}.connect`)}
          </Button>
        )}
      </div>
      <ConfirmationModal
        isOpen={isDisconnectModalOpen}
        setIsOpen={setIsDisconnectModalOpen}
        title={t(`${TRANSLATION_PREFIX}.disconnectModal.title`, { name })}
        confirmationText=""
        handleSubmit={handleConfirmDisconnect}
        submitButtonText={t(`${TRANSLATION_PREFIX}.menu.disconnect`)}
        hideDivider
        headerClassname="border-none"
      />
    </>
  );
};

export default ServiceAction;
